import { Funnel } from "@phosphor-icons/react";
import clsx from "clsx";
import { SortOrder } from "../../api/common/models";
import {
    EmissionsSourceRequiredEvidenceOrderBy,
    RequiredEvidenceStatusFilter,
} from "../../api/models/RequiredEvidence";
import { EvidenceType } from "../../api/models/Evidence";
import MultiSelectDropdown, {
    MultiSelectDropdownItemOption,
} from "../MultiSelectFilterToolbarDropdown";
import { useEvidenceTypes } from "../../api/evidence";
import useCollapsibleContent from "../../hooks/useCollapsibleContent";
import { toggleItemInArray } from "../../utils/toggleItemInArray";
import Button from "../Button";
import FilterChip from "../FilterChip";
import FilterChipGroup from "../FilterChipGroup";
import Separator from "../Separator";
import SearchBar from "../SearchBar";
import ResponsiveGroupedItemsCarousel from "../ResponsiveGroupedItemsCarousel";
import SingleSelectDropdown from "../SingleSelectFilterToolbarDropdown";

type RequiredEvidenceFilterProps = {
    statusFilterOptions: {
        value: RequiredEvidenceStatusFilter;
        label: string;
    }[];
    statusFilter: RequiredEvidenceStatusFilter;
    setStatusFilter: React.Dispatch<
        React.SetStateAction<RequiredEvidenceStatusFilter>
    >;
    evidenceTypeIds: number[];
    searchTerm: string;
    orderBy: EmissionsSourceRequiredEvidenceOrderBy;
    sortOrder: SortOrder;
    additionalActions?: React.ReactNode;
    setEvidenceTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
};

const convertEvidenceTypesToMultiSelectDropdownOptions = (
    evidenceTypes: EvidenceType[]
): MultiSelectDropdownItemOption<number>[] =>
    evidenceTypes.map((evidenceType) => ({
        label: evidenceType.name,
        value: evidenceType.id,
    }));

const RequiredEvidenceFilter = ({
    statusFilterOptions,
    statusFilter,
    setStatusFilter,
    evidenceTypeIds,
    setEvidenceTypeIds,
    additionalActions,
    searchTerm,
    setSearchTerm,
}: RequiredEvidenceFilterProps) => {
    const { evidenceTypes } = useEvidenceTypes();

    const { expanded, toggle, triggerProps, expandableContentProps } =
        useCollapsibleContent();

    const evidenceTypeOptions =
        convertEvidenceTypesToMultiSelectDropdownOptions(evidenceTypes);

    const handleEvidenceTypeSelect = (value: number) =>
        setEvidenceTypeIds(toggleItemInArray(evidenceTypeIds, value));

    const clearAllFilters = () => {
        setSearchTerm("");
        setEvidenceTypeIds([]);
        setStatusFilter("All");
    };

    const showClearAllButton =
        searchTerm !== "" ||
        evidenceTypeIds.length > 0 ||
        statusFilter !== "All";

    const filterDropdowns = [
        <SingleSelectDropdown
            label="Status"
            name="Status"
            options={statusFilterOptions}
            selected={statusFilter}
            onSelect={setStatusFilter}
            onReset={() => setStatusFilter("All")}
            key="filter-dropdown-status"
        />,
        <MultiSelectDropdown
            label="Evidence Types"
            selected={evidenceTypeIds}
            options={evidenceTypeOptions}
            onSelect={handleEvidenceTypeSelect}
            onClear={() => setEvidenceTypeIds([])}
            key="filter-dropdown-evidence-types"
        />,
    ];

    const statusFilterLabel = statusFilterOptions.find(
        (option) => option.value === statusFilter
    )?.label;

    return (
        <>
            <div className="flex items-center p-5">
                <Button
                    variant="outline"
                    onClick={toggle}
                    {...triggerProps}
                    className="flex-none"
                >
                    <Funnel width={20} height={20} className="mr-2" />
                    {expanded ? "Hide" : "Show"} filters & sorting
                </Button>
                <div className="flex gap-2 flex-wrap pl-2">
                    {statusFilter !== "All" && (
                        <FilterChip onRemove={() => setStatusFilter("All")}>
                            {statusFilterLabel}
                        </FilterChip>
                    )}
                    {searchTerm !== "" && (
                        <FilterChip onRemove={() => setSearchTerm("")}>
                            '{searchTerm}'
                        </FilterChip>
                    )}
                    <FilterChipGroup
                        selectedItemIds={evidenceTypeIds}
                        allItems={evidenceTypes}
                        labelKey="name"
                        onRemove={setEvidenceTypeIds}
                    />
                    {showClearAllButton && (
                        <Button variant="outline" onClick={clearAllFilters}>
                            Clear all
                        </Button>
                    )}
                </div>
                <div className="ml-auto flex-none">{additionalActions}</div>
            </div>
            <Separator />
            <div
                className={clsx("pt-5 px-4", !expanded && "hidden")}
                {...expandableContentProps}
            >
                <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search..."
                    additionalSearchElements={
                        <ResponsiveGroupedItemsCarousel
                            items={filterDropdowns}
                            prevTooltipLabel="See previous group of filters"
                            nextTooltipLabel="See next group of filters"
                        />
                    }
                />
            </div>
        </>
    );
};

export default RequiredEvidenceFilter;
